<script lang="ts">
    import type { CreateScheduleDto } from '../../modules/schedulerApi/schedules/dto/create-schedule.dto'
    import type { UpdateScheduleDto } from '../../modules/schedulerApi/schedules/dto/update-schedule.dto'
    import { Schedule } from '../../modules/schedulerApi/schedules/schedule'
    import { SchedulesRepository } from '../../modules/schedulerApi/schedules/schedules.repository'
    import { SchedulesService } from '../../modules/schedulerApi/schedules/schedules.service'
    import ObSchedulerItem from './ob-scheduler-item.svelte'
    import ObStep from './ob-step.svelte'
    import Zbutton from '../_common/z/zbutton.svelte'
    import Zinput from '../_common/z/zinput.svelte'
    import ZContainer from '../_common/z/z-container.svelte'
    import LoadSpinner from '../_common/load-spinner.svelte'
    import { faAngleLeft, faAngleRight } from '@fortawesome/free-solid-svg-icons'
    import { createEventDispatcher, onMount } from 'svelte/internal'
    const dispatch = createEventDispatcher()
    let min = 0
    let total = 0
    let schedules = []
    let show = false
    let loaded = false
    const rep = new SchedulesRepository()
    const service = new SchedulesService(rep)

    // o Zinput type="time" trabalha com Date (entra Date, sai Date) — nunca com string 'HH:mm'
    let startTime: Date = buildTime(8, 0)
    let endTime: Date = buildTime(21, 0)

    function buildTime(hour: number, minute: number): Date {
        const date = new Date()
        date.setHours(hour, minute, 0, 0)
        return date
    }

    function dateToMinute(date): number {
        const value = date instanceof Date ? date : new Date(date)
        if (isNaN(value.getTime())) return null
        return value.getHours() * 60 + value.getMinutes()
    }

    // a lista começa no que já existe: não dá para ter menos agendas do que as já cadastradas
    $: totalOptions = Array.from({ length: 30 }, (_, index) => ({ value: index + min, label: String(index + min) }))

    function totalChange() {
        while (schedules.length != total) {
            if (schedules.length > total) {
                schedules.pop()
                continue
            }
            if (schedules.length < total) {
                let s = new Schedule()
                s.position = schedules.length + 1
                s.range = 15
                s.startMinute = dateToMinute(startTime)
                s.endMinute = dateToMinute(endTime)
                schedules.push(s)
            }
        }
        schedules = schedules
    }

    function onBack() {
        if (schedules.length > 0) {
            if (!confirm('As alterações dessa tela serão perdidas ao voltar. Deseja mesmo voltar?')) return false
        }
        dispatch('back')
    }

    let saving = false
    async function onNext() {
        if (saving) return
        saving = true
        if (total === 0) {
            window.error('Informe a quantidade de agendas necessárias')
            saving = false
            return
        }
        if (await saveEntity()) {
            dispatch('next')
        }
        saving = false
    }

    onMount(async () => {
        await loadEntities()
    })

    async function loadEntities() {
        try {
            show = true
            const result = await service.getAll()
            min = result.length
            total = result.length
            schedules = result
            loaded = true
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    async function createEntity(entity) {
        try {
            show = true

            const dto: CreateScheduleDto = {
                endMinute: entity.endMinute,
                label: entity.label,
                position: entity.position,
                range: entity.range,
                startMinute: entity.startMinute,
                picture: null,
                public: true,
                slots: null,
            }
            entity = await service.create(dto)
            return true
        } catch (error) {
            window.error(error)
            return false
        } finally {
            show = false
        }
    }

    async function updateEntity(entity) {
        try {
            show = true
            const dto: UpdateScheduleDto = {
                endMinute: entity.endMinute,
                label: entity.label,
                position: entity.position,
                range: entity.range,
                startMinute: entity.startMinute,
            }
            entity = await service.update(entity.scheduleId, dto)
            return true
        } catch (error) {
            window.error(error)
            return false
        } finally {
            show = false
        }
    }

    function validateAll() {
        for (let entity of schedules) {
            if (!validateFields(entity)) return false
        }
        return true
    }

    async function saveEntity() {
        if (!validateAll()) return false
        for (let entity of schedules) {
            if (entity.scheduleId) {
                if (!(await updateEntity(entity))) return false
            } else {
                if (!(await createEntity(entity))) return false
            }
        }
        return true
    }

    function validateFields(entity) {
        if (!entity.label) {
            window.error('Informe o nome da agenda')
            return false
        }
        if (!Number.isFinite(entity.startMinute) || !Number.isFinite(entity.endMinute)) {
            window.error('Informe o horário de início e fim das atividades')
            return false
        }
        if (entity.range < 10 || entity.range > 1440) {
            window.error([
                'Informe o intervalo entre um agendamento e outro.',
                'O intervalo mínimo é de 10 minutos',
                'O intervalo máximo é de 1440 minutos (24 horas)',
            ])
            return false
        }
        return true
    }
</script>

<ObStep title="Agenda" label="Defina quantas agendas você precisa para começar">
    <svelte:fragment slot="acts">
        <Zbutton color="secondary" icon={faAngleLeft} label="Voltar" on:click={onBack} />
        <Zbutton color="primary" icon={faAngleRight} label="Avançar" loading={saving} disabled={saving} on:click={onNext} />
    </svelte:fragment>

    <ZContainer gapRow="8px" gapCol="8px">
        <Zinput size={3} sizeM={6} label="Horário de funcionamento" type="time" bind:value={startTime} />
        <Zinput size={3} sizeM={6} label="Até" type="time" bind:value={endTime} />
        <Zinput size={3} sizeM={12} label="Total de agendas" type="select" options={totalOptions} bind:value={total} on:change={totalChange} />
    </ZContainer>

    {#each schedules as schedule}
        <ObSchedulerItem bind:schedule />
    {/each}

    {#if schedules.length > 0}
        <div class="mt-3 text-muted">
            <b>Nome:</b><br />
            Escolha de acordo com sua necessidade.<br />
            Ex: 'Sala do laser' / 'Cadeira 1' / 'João da Silva' / 'Sala de avaliação'
        </div>
        <div class="mt-3 text-muted">
            <b>Intervalo:</b><br />
            É o tempo em minutos que sua agenda é dividida.<br />
            Ex: ao definir '15' ela terá 4 divisões a cada hora.<br />
            Ex: ao definir '60' ela terá 1 divisão a cada hora.<br />
            Se estiver com dúvidas deixe '15' e você poderá ajustar esse valor depois.
        </div>
    {/if}
</ObStep>

<LoadSpinner {show} />
